import type { TContextMenuItem } from '@/client/components/Layout/LayoutManager'
import tabsStore, { TTabInput } from '@client/stores/tabsStore'
import type { TFunction } from 'i18next'

/**
 * Generic tree helper: find a node by id in a nested RSuite treeNodes structure.
 */
export function findTreeNodeById(treeNodes: any[] | undefined, id: string) {
    const walk = (nodes?: any[]): any | null => {
        if (!nodes) return null
        for (const n of nodes) {
            if (!n) continue
            if (n.id === id) return n
            if (n.children) {
                const found = walk(n.children)
                if (found) return found
            }
        }
        return null
    }
    return walk(treeNodes)
}

/**
 * - t: translation function
 * - getItemById: how to retrieve the underlying item by id (node or row)
 * - addTab: LayoutManager.addTab
 * - openTabToRight: LayoutManager.openTabToRight
 * - createTab: (item) => TTabInput
 */
export function createDefaultListContextMenuItems<TItem>(opts: {
    t: TFunction
    getItemById: (id: string) => TItem | null
    addTab: (tab: TTabInput) => void
    openTabToRight: (tab: TTabInput) => void
    createTab: (item: TItem) => TTabInput
    removeTabCopies: (sourceTabId: string) => void
    removeOtherTabs: (sourceTabId: string) => void
}): TContextMenuItem[] {
    const { t, getItemById, addTab, openTabToRight, createTab, removeTabCopies, removeOtherTabs } = opts

    const items: TContextMenuItem[] = []

    items.push({
        label: t('Open to the Right'),
        onClick: (_event, current) => {
            const item = getItemById(current.id)
            if (!item) return
            const tab = createTab(item)
            openTabToRight(tab)
        },
    })

    items.push({
        label: t('Remove tab copies'),
        onClick: (_event, current) => {
            const item = getItemById(current.id)
            if (!item) return
            const dummyTab = createTab(item)
            const opened = tabsStore.findTabByData(dummyTab.dataId)
            if (!opened) return
            removeTabCopies(opened.id)
        },
    })

    items.push({
        label: t('Remove other tabs'),
        onClick: (_event, current) => {
            const item = getItemById(current.id)
            if (!item) return
            const dummyTab = createTab(item)
            const opened = tabsStore.findTabByData(dummyTab.dataId)
            if (!opened) return
            removeOtherTabs(opened.id)
        },
    })

    return items
}
